/* Smart link (fan link) page behaviour.
 *
 * Handles: outbound service clicks, native app deep links, Apple storefront
 * geo-swapping, the fan capture form (ToneDen-style triggers) and analytics.
 */

const SMART_LINK_STORAGE_KEY = 'lb:smart-link:fan';

/* Platforms whose native apps are reached more reliably by custom scheme than
 * by their https universal link. Everything else is left as https so the OS
 * can hand off to the app itself. */
const SMART_LINK_SCHEMES = {
  spotify(url) {
    const parts = url.pathname.split('/').filter((part) => part && !part.startsWith('intl-'));
    if (parts.length < 2) return null;
    return `spotify:${parts[0]}:${parts[1]}`;
  },
  apple_music(url) {
    return `music://${url.host}${url.pathname}${url.search}`;
  },
  itunes(url) {
    return `music://${url.host}${url.pathname}${url.search}`;
  },
  deezer(url) {
    return `deezer://${url.host}${url.pathname}`;
  },
  tidal(url) {
    const parts = url.pathname.split('/').filter(Boolean);
    const index = parts.findIndex((part) =>
      ['album', 'track', 'playlist', 'video', 'artist', 'mix'].includes(part)
    );
    if (index === -1 || !parts[index + 1]) return null;
    return `tidal://${parts[index]}/${parts[index + 1]}`;
  },
};

/* Storefront-scoped hosts: the first path segment is a country code we can swap. */
const SMART_LINK_STOREFRONT_HOSTS = ['music.apple.com', 'geo.music.apple.com', 'itunes.apple.com'];

/* Timezone is a better proxy for physical location than navigator.language,
 * and needs no network request or consent. Covers the markets that matter for
 * storefront swapping; anything else falls back to the link as authored. */
const SMART_LINK_TIMEZONE_COUNTRIES = {
  'America/New_York': 'us',
  'America/Detroit': 'us',
  'America/Chicago': 'us',
  'America/Denver': 'us',
  'America/Phoenix': 'us',
  'America/Los_Angeles': 'us',
  'America/Anchorage': 'us',
  'Pacific/Honolulu': 'us',
  'America/Toronto': 'ca',
  'America/Vancouver': 'ca',
  'America/Edmonton': 'ca',
  'America/Winnipeg': 'ca',
  'America/Halifax': 'ca',
  'America/Mexico_City': 'mx',
  'America/Bogota': 'co',
  'America/Lima': 'pe',
  'America/Santiago': 'cl',
  'America/Argentina/Buenos_Aires': 'ar',
  'America/Sao_Paulo': 'br',
  'Europe/London': 'gb',
  'Europe/Dublin': 'ie',
  'Europe/Lisbon': 'pt',
  'Europe/Madrid': 'es',
  'Europe/Paris': 'fr',
  'Europe/Brussels': 'be',
  'Europe/Amsterdam': 'nl',
  'Europe/Berlin': 'de',
  'Europe/Zurich': 'ch',
  'Europe/Vienna': 'at',
  'Europe/Rome': 'it',
  'Europe/Copenhagen': 'dk',
  'Europe/Oslo': 'no',
  'Europe/Stockholm': 'se',
  'Europe/Helsinki': 'fi',
  'Europe/Warsaw': 'pl',
  'Europe/Prague': 'cz',
  'Europe/Budapest': 'hu',
  'Europe/Bucharest': 'ro',
  'Europe/Athens': 'gr',
  'Europe/Istanbul': 'tr',
  'Europe/Kyiv': 'ua',
  'Europe/Moscow': 'ru',
  'Africa/Lagos': 'ng',
  'Africa/Johannesburg': 'za',
  'Africa/Cairo': 'eg',
  'Asia/Jerusalem': 'il',
  'Asia/Dubai': 'ae',
  'Asia/Karachi': 'pk',
  'Asia/Kolkata': 'in',
  'Asia/Calcutta': 'in',
  'Asia/Bangkok': 'th',
  'Asia/Jakarta': 'id',
  'Asia/Singapore': 'sg',
  'Asia/Kuala_Lumpur': 'my',
  'Asia/Manila': 'ph',
  'Asia/Hong_Kong': 'hk',
  'Asia/Taipei': 'tw',
  'Asia/Shanghai': 'cn',
  'Asia/Seoul': 'kr',
  'Asia/Tokyo': 'jp',
  'Australia/Sydney': 'au',
  'Australia/Melbourne': 'au',
  'Australia/Brisbane': 'au',
  'Australia/Perth': 'au',
  'Pacific/Auckland': 'nz',
};

function smartLinkDetectCountry() {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && SMART_LINK_TIMEZONE_COUNTRIES[zone]) return SMART_LINK_TIMEZONE_COUNTRIES[zone];
  } catch (error) {
    // Intl unavailable — fall through to the language tag.
  }

  const tags = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
  for (let index = 0; index < tags.length; index += 1) {
    const match = /-([a-z]{2})(?:$|-)/i.exec(tags[index] || '');
    if (match) return match[1].toLowerCase();
  }

  return null;
}

class SmartLink extends HTMLElement {
  constructor() {
    super();

    this.settings = {
      release: this.dataset.release || '',
      artist: this.dataset.artist || '',
      deepLinks: this.dataset.deepLinks === 'true',
      geo: this.dataset.geo === 'true',
      newTab: this.dataset.newTab === 'true',
      trigger: this.dataset.captureTrigger || 'off',
      delay: parseInt(this.dataset.captureDelay || '5', 10),
      required: this.dataset.captureRequired === 'true',
      remember: this.dataset.captureRemember === 'true',
      provider: this.dataset.captureProvider || 'shopify',
      klaviyoCompany: this.dataset.klaviyoCompany || '',
      klaviyoList: this.dataset.klaviyoList || '',
      webhook: this.dataset.webhook || '',
      trackView: this.dataset.trackView === 'true',
    };

    this.services = Array.from(this.querySelectorAll('[data-smart-link-service]'));
    this.serviceList = this.querySelector('[data-smart-link-services]');
    this.modal = this.querySelector('[data-smart-link-modal]');
    this.capture = this.querySelector('[data-smart-link-capture]');
    this.form = this.querySelector('[data-smart-link-form]');
    this.emailInput = this.querySelector('[data-smart-link-email]');
    this.nameInput = this.querySelector('[data-smart-link-name]');
    this.errorEl = this.querySelector('[data-smart-link-error]');
    this.successEl = this.querySelector('[data-smart-link-success]');
    this.skipButton = this.querySelector('[data-smart-link-skip]');
    this.closeButton = this.querySelector('[data-smart-link-close]');

    this.pendingService = null;
    this.country = null;
    this.designMode = Boolean(window.Shopify && window.Shopify.designMode);
    this.isMobile = /android|iphone|ipad|ipod|windows phone/i.test(navigator.userAgent);
  }

  connectedCallback() {
    if (this.settings.geo) this.country = smartLinkDetectCountry();

    this.services.forEach((service) => {
      service.addEventListener('click', this.onServiceClick.bind(this));
    });

    if (this.form) this.form.addEventListener('submit', this.onSubmit.bind(this));
    if (this.skipButton) this.skipButton.addEventListener('click', this.onSkip.bind(this));
    if (this.closeButton) this.closeButton.addEventListener('click', () => this.closeModal());
    if (this.modal) {
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal && !this.settings.required) this.closeModal();
      });
    }
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.modal && !this.modal.hidden && !this.settings.required) {
        this.closeModal();
      }
    });

    if (this.settings.trackView) {
      this.track('smart_link_view', { platform: null });
    }

    this.setUpCaptureTrigger();
  }

  /* ---------- capture triggers ---------- */

  get captured() {
    if (this.designMode) return false;
    if (!this.settings.remember) return this.sessionCaptured === true;
    try {
      return Boolean(window.localStorage.getItem(SMART_LINK_STORAGE_KEY));
    } catch (error) {
      return this.sessionCaptured === true;
    }
  }

  rememberFan(email) {
    this.sessionCaptured = true;
    if (!this.settings.remember || this.designMode) return;
    try {
      window.localStorage.setItem(
        SMART_LINK_STORAGE_KEY,
        JSON.stringify({ email: email || '', at: new Date().toISOString() })
      );
    } catch (error) {
      // Private mode / storage disabled — session state is enough.
    }
  }

  setUpCaptureTrigger() {
    const { trigger, delay } = this.settings;
    if (trigger === 'off' || !this.capture) return;

    /* Inline: the form is part of the page, so there is nothing to trigger.
     * A fan who already signed up sees the confirmation instead of the form. */
    if (trigger === 'inline') {
      if (this.captured) this.showSuccess();
      return;
    }

    if (trigger === 'gate') {
      if (this.captured) {
        this.revealServices(true);
      } else {
        this.showInlineCapture();
      }
      return;
    }

    if (this.captured) return;

    if (trigger === 'load') {
      this.openModal();
    } else if (trigger === 'delay') {
      window.setTimeout(() => {
        if (!this.captured) this.openModal();
      }, Math.max(0, delay) * 1000);
    } else if (trigger === 'exit') {
      const onExit = () => {
        if (!this.captured) this.openModal();
      };
      document.addEventListener(
        'mouseout',
        (event) => {
          if (!event.relatedTarget && event.clientY <= 10) onExit();
        },
        { once: true }
      );
      // Touch devices have no exit intent — fall back to a timed prompt.
      window.setTimeout(onExit, Math.max(delay, 15) * 1000);
    }
  }

  showInlineCapture() {
    if (this.serviceList) this.serviceList.hidden = true;
    this.capture.hidden = false;
    if (this.skipButton) this.skipButton.hidden = this.settings.required;
  }

  /* hideCapture is false right after a signup so the fan still sees the
   * confirmation sitting above the links they just unlocked. */
  revealServices(hideCapture) {
    if (this.serviceList) this.serviceList.hidden = false;
    if (hideCapture && this.capture) this.capture.hidden = true;
  }

  openModal() {
    if (!this.modal || !this.capture) return;
    this.capture.hidden = false;
    this.modal.hidden = false;
    if (this.closeButton) this.closeButton.hidden = this.settings.required;
    if (this.skipButton) this.skipButton.hidden = this.settings.required;
    document.body.classList.add('overflow-hidden');
    if (this.emailInput) {
      window.setTimeout(() => this.emailInput.focus({ preventScroll: true }), 50);
    }
  }

  closeModal() {
    if (!this.modal) return;
    this.modal.hidden = true;
    document.body.classList.remove('overflow-hidden');
    const pending = this.pendingService;
    this.pendingService = null;
    if (pending) this.proceed(pending);
  }

  /* ---------- clicks ---------- */

  onServiceClick(event) {
    const service = event.currentTarget;
    event.preventDefault();

    const needsCapture =
      this.settings.trigger === 'click' && this.capture && !this.captured && !this.designMode;

    if (needsCapture) {
      this.pendingService = service;
      this.openModal();
      return;
    }

    this.proceed(service);
  }

  proceed(service) {
    const platform = service.dataset.platform || 'custom';
    const label = service.dataset.label || platform;
    const target = this.resolveUrl(service.dataset.url);

    this.track('smart_link_click', { platform, label, url: target });

    /* Deep links replace the current page, so they are only worth it on mobile,
     * where app handoff matters and same-tab navigation is expected anyway. On
     * desktop the https link opens normally and honours the new-tab setting. */
    const scheme =
      this.settings.deepLinks && (this.isMobile || !this.settings.newTab)
        ? this.deepLink(target, platform)
        : null;

    if (scheme) {
      const fallback = window.setTimeout(() => {
        if (document.visibilityState === 'visible') window.location.href = target;
      }, 1400);
      const cancel = () => window.clearTimeout(fallback);
      window.addEventListener('pagehide', cancel, { once: true });
      window.addEventListener('blur', cancel, { once: true });
      document.addEventListener(
        'visibilitychange',
        () => {
          if (document.visibilityState === 'hidden') cancel();
        },
        { once: true }
      );
      window.location.href = scheme;
      return;
    }

    if (this.settings.newTab) {
      /* No 'noopener' in the features string: that makes window.open return
       * null even on success, which reads as "blocked" and would navigate this
       * tab as well. Severing opener on the handle does the same job. */
      const opened = window.open(target, '_blank');
      if (opened) {
        try {
          opened.opener = null;
        } catch (error) {
          // Cross-origin handle; the anchor's rel="noopener" already covers it.
        }
        return;
      }
    }

    window.location.href = target;
  }

  resolveUrl(rawUrl) {
    if (!rawUrl) return '#';
    if (!this.settings.geo || !this.country) return rawUrl;

    let url;
    try {
      url = new URL(rawUrl, window.location.origin);
    } catch (error) {
      return rawUrl;
    }

    if (!SMART_LINK_STOREFRONT_HOSTS.includes(url.host)) return rawUrl;

    const parts = url.pathname.split('/');
    if (parts[1] && /^[a-z]{2}$/i.test(parts[1]) && parts[1].toLowerCase() !== this.country) {
      parts[1] = this.country;
      url.pathname = parts.join('/');
      return url.toString();
    }

    return rawUrl;
  }

  deepLink(rawUrl, platform) {
    const builder = SMART_LINK_SCHEMES[platform];
    if (!builder) return null;
    try {
      return builder(new URL(rawUrl));
    } catch (error) {
      return null;
    }
  }

  /* ---------- capture submit ---------- */

  onSkip(event) {
    event.preventDefault();
    this.track('smart_link_capture_skip', { platform: null });
    if (this.settings.trigger === 'gate') {
      this.revealServices(true);
      return;
    }
    this.closeModal();
  }

  onSubmit(event) {
    event.preventDefault();

    const email = (this.emailInput && this.emailInput.value || '').trim();
    if (!email || !/^[^@\s]+@[^@\s.]+\.[^@\s]+$/.test(email)) {
      this.showError(true);
      if (this.emailInput) this.emailInput.focus();
      return;
    }
    this.showError(false);

    const firstName = (this.nameInput && this.nameInput.value || '').trim();

    // Fired without awaiting: the fan should never wait on our list provider.
    this.sendLead(email, firstName);
    this.track('smart_link_lead', { platform: null, email_domain: email.split('@')[1] || '' });
    this.rememberFan(email);

    if (this.settings.trigger === 'gate') {
      this.showSuccess();
      this.revealServices(false);
      return;
    }

    if (this.pendingService) {
      this.closeModal();
      return;
    }

    this.showSuccess();
    // Inline forms stay put; modal ones dismiss themselves once confirmed.
    if (this.settings.trigger !== 'inline') {
      window.setTimeout(() => this.closeModal(), 1200);
    }
  }

  showError(show) {
    if (this.errorEl) this.errorEl.hidden = !show;
  }

  showSuccess() {
    if (this.form) this.form.hidden = true;
    if (this.skipButton) this.skipButton.hidden = true;
    if (this.successEl) this.successEl.hidden = false;
  }

  sendLead(email, firstName) {
    const { provider, klaviyoCompany, klaviyoList, webhook, release, artist } = this.settings;

    if (provider === 'klaviyo' && klaviyoCompany && klaviyoList) {
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            custom_source: `Smart link: ${release || document.title}`,
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email,
                  first_name: firstName || undefined,
                  properties: { smart_link_release: release, smart_link_artist: artist },
                },
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: klaviyoList } } },
        },
      };
      this.request(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(klaviyoCompany)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify(payload),
      });
      return;
    }

    if (provider === 'webhook' && webhook) {
      this.request(webhook, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          email,
          first_name: firstName,
          release,
          artist,
          url: window.location.href,
          referrer: document.referrer || null,
        }),
      });
      return;
    }

    /* Default, and the deliberate fallback when Klaviyo or the webhook is
     * selected but not configured: an address never gets dropped on the floor. */
    const body = new FormData();
    body.append('form_type', 'customer');
    body.append('utf8', '✓');
    body.append('contact[email]', email);
    if (firstName) body.append('contact[first_name]', firstName);
    body.append('contact[tags]', ['newsletter', 'smart-link', release].filter(Boolean).join(','));
    this.request('/contact', { method: 'POST', body });
  }

  request(url, options) {
    try {
      fetch(url, Object.assign({ keepalive: true, mode: 'cors' }, options)).catch(() => {});
    } catch (error) {
      // Nothing more we can do client-side; the fan still gets their music.
    }
  }

  /* ---------- analytics ---------- */

  track(event, detail) {
    const payload = Object.assign(
      {
        release: this.settings.release,
        artist: this.settings.artist,
        page_path: window.location.pathname,
      },
      detail || {}
    );

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event }, payload));

    if (typeof window.gtag === 'function') {
      window.gtag('event', event, payload);
    }

    /* ToneDen parity: the click-through to a service is the ViewContent, not
     * the page view. Existing audiences and lookalikes keep working. The page
     * view is already covered by the store-wide pixel's PageView. */
    if (typeof window.fbq === 'function') {
      if (event === 'smart_link_click') {
        window.fbq('track', 'ViewContent', {
          content_name: payload.release,
          content_category: payload.platform,
          content_type: 'music',
        });
        window.fbq('trackCustom', 'SmartLinkClick', payload);
      } else if (event === 'smart_link_lead') {
        window.fbq('track', 'Lead', { content_name: payload.release });
      }
    }

    if (window.ttq && typeof window.ttq.track === 'function') {
      if (event === 'smart_link_click') {
        window.ttq.track('ViewContent', { content_name: payload.release, content_type: 'music' });
      } else if (event === 'smart_link_lead') {
        window.ttq.track('SubmitForm', { content_name: payload.release });
      }
    }

    if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
      window.Shopify.analytics.publish(event, payload);
    }

    if (this.settings.webhook && this.settings.provider !== 'webhook' && navigator.sendBeacon) {
      try {
        navigator.sendBeacon(this.settings.webhook, JSON.stringify(Object.assign({ event }, payload)));
      } catch (error) {
        // Beaconing is best-effort.
      }
    }

    this.dispatchEvent(new CustomEvent(`smart-link:${event}`, { bubbles: true, detail: payload }));
  }
}

if (!customElements.get('smart-link')) {
  customElements.define('smart-link', SmartLink);
}
